"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFolderRestrictions = void 0;
const tslib_1 = require("tslib");
const react_1 = require("react");
const ui_common_translation_1 = require("@desygner/ui-common-translation");
const ui_core_api_gateway_1 = require("@desygner/ui-core-api-gateway");
const ui_editor_data_access_assets_1 = require("@desygner/ui-editor-data-access-assets");
function formatFolder(type, folder) {
    const folderType = {
        text: 'text',
        image: 'image',
        video: 'video',
        logo: 'logo',
        icon: 'icon',
    };
    return folder
        .filter((folder) => folder.data.types.includes(folderType[type]))
        .map((folder) => ({
        resourceId: folder.data.id,
        folderId: folder.id,
        name: folder.data.name,
    }));
}
function formatSubFolder(resources) {
    const data = {
        folders: [],
    };
    if (!(resources === null || resources === void 0 ? void 0 : resources.length)) {
        return data;
    }
    return resources.reduce((acc, resource) => {
        if (resource.type.toLowerCase() === 'folder') {
            acc.folders.push({
                resourceId: resource.data.id,
                folderId: resource.id,
                name: resource.data.name,
            });
        }
        return acc;
    }, data);
}
function useFolderRestrictions({ type, workspace, credentials, handleFolderSelected: handleFolderSelectedProp, selectedFolders: selectedFoldersProps, maximumShowingCharacters = 30, }) {
    const { t } = (0, ui_common_translation_1.useTranslation)();
    const allFolderId = 'all';
    const rootFolder = {
        resourceId: allFolderId,
        folderId: allFolderId,
        name: t('tool.restrictions.all_workspaces_assets'),
        isRoot: true,
    };
    const [folders, setFolders] = (0, react_1.useState)();
    const serviceApi = (0, react_1.useMemo)(() => {
        ui_editor_data_access_assets_1.assetContainer
            .get(ui_core_api_gateway_1.TYPES.ApiGateway)
            .configure(credentials.baseUrl, credentials.userToken, workspace);
        return ui_editor_data_access_assets_1.assetContainer.get(ui_editor_data_access_assets_1.TYPES.WorkspaceAssets);
    }, []);
    const [selectedFolders, setSelectedFolders] = (0, react_1.useState)(selectedFoldersProps);
    const [selectedFolderChips, setSelectedChips] = (0, react_1.useState)({
        showingNames: [],
        hidingNames: [],
    });
    const [selectedParentFolders, setSelectedParentFolders] = (0, react_1.useState)(selectedFoldersProps);
    const [openingFolders, setOpeningFolders] = (0, react_1.useState)({});
    (0, react_1.useEffect)(() => {
        serviceApi.fetchFolders().then(function (response) {
            return tslib_1.__awaiter(this, void 0, void 0, function* () {
                const folders = formatFolder(type, response);
                rootFolder.folder = folders;
                setFolders([rootFolder]);
                yield Promise.all(Object.keys(selectedFoldersProps).map((selectedFolderId) => handleFolderSelected(folders.find((folder) => folder.resourceId.toString() === selectedFolderId), !!selectedFoldersProps[selectedFolderId])));
            });
        });
    }, []);
    const buildFolderTree = (0, react_1.useCallback)(function (folder, resourceId, subFolders) {
        var _a, _b;
        if (folder.resourceId === resourceId) {
            folder.folder = subFolders;
            folder.isLoaded = true;
        }
        if (((_b = (_a = folder === null || folder === void 0 ? void 0 : folder.folder) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) === 0) {
            return folder;
        }
        if (folder.folder) {
            folder.folder = folder.folder.map((subFolder) => buildFolderTree(subFolder, resourceId, subFolders));
        }
        return folder;
    }, []);
    const fetchSubFolder = (0, react_1.useCallback)((resourceId) => tslib_1.__awaiter(this, void 0, void 0, function* () {
        const subFolders = yield serviceApi.fetchResourceFromFolder(resourceId.toString());
        const formattedSubFolders = formatSubFolder(subFolders);
        setFolders((prevFolders) => {
            if (prevFolders === null) {
                return null;
            }
            return prevFolders === null || prevFolders === void 0 ? void 0 : prevFolders.map((folder) => buildFolderTree(folder, resourceId, formattedSubFolders.folders));
        });
        return formattedSubFolders.folders;
    }), [buildFolderTree, serviceApi]);
    const loadSubFolders = (0, react_1.useCallback)((folder) => tslib_1.__awaiter(this, void 0, void 0, function* () {
        if (!folder.isLoaded) {
            return yield fetchSubFolder(folder.resourceId);
        }
        else {
            return folder.folder;
        }
    }), [fetchSubFolder]);
    const fetchSubFolderRecursively = (0, react_1.useCallback)((folder) => tslib_1.__awaiter(this, void 0, void 0, function* () {
        let subFolders = [];
        if (folder === undefined) {
            return [];
        }
        if (folder.isRoot) {
            return [folder];
        }
        subFolders = yield loadSubFolders(folder);
        if (subFolders === undefined || subFolders.length === 0) {
            return [folder];
        }
        subFolders = (yield Promise.all(subFolders.map((subFolder) => fetchSubFolderRecursively(subFolder)))).flat();
        return [folder, ...subFolders];
    }), [loadSubFolders]);
    const getSubFolderRecursively = (0, react_1.useCallback)((folder) => {
        let subFolders = [];
        if (folder === undefined) {
            return [];
        }
        subFolders = folder.folder;
        if (subFolders === undefined || subFolders.length === 0) {
            return [folder];
        }
        subFolders = subFolders
            .map((subFolder) => getSubFolderRecursively(subFolder))
            .flat();
        return [folder, ...subFolders];
    }, []);
    const handleFolderOpening = (0, react_1.useCallback)((folder, opened) => tslib_1.__awaiter(this, void 0, void 0, function* () {
        if (opened) {
            yield loadSubFolders(folder);
            setOpeningFolders((prevState) => (Object.assign(Object.assign({}, prevState), { [folder.resourceId]: {
                    folderId: folder.folderId,
                    resourceId: folder.resourceId,
                } })));
        }
        else {
            const affectedFolders = getSubFolderRecursively(folder);
            setOpeningFolders((prevState) => {
                const newState = Object.assign({}, prevState);
                for (const affectedFolder of affectedFolders) {
                    delete newState[affectedFolder.resourceId];
                }
                return newState;
            });
        }
    }), [getSubFolderRecursively, loadSubFolders]);
    const handleFolderSelected = (0, react_1.useCallback)((folder, selected) => tslib_1.__awaiter(this, void 0, void 0, function* () {
        if (!folder) {
            return;
        }
        if (folder.isRoot) {
            const allFolders = selected
                ? {
                    [folder.resourceId]: {
                        folderId: folder.folderId,
                        resourceId: folder.resourceId,
                    },
                }
                : {};
            setSelectedFolders(allFolders);
            setSelectedParentFolders(allFolders);
            handleFolderSelectedProp(selected ? [allFolders[folder.resourceId]] : []);
            return;
        }
        const affectedFolders = yield fetchSubFolderRecursively(folder);
        setSelectedFolders((prevState) => {
            const newState = Object.assign({}, prevState);
            for (const affectedFolder of affectedFolders) {
                if (selected) {
                    newState[affectedFolder.resourceId] = {
                        folderId: affectedFolder.folderId,
                        resourceId: affectedFolder.resourceId,
                    };
                }
                else {
                    delete newState[affectedFolder.resourceId];
                }
            }
            delete newState[allFolderId];
            return newState;
        });
        setSelectedParentFolders((prevState) => {
            const newState = Object.assign({}, prevState);
            if (selected) {
                newState[folder.resourceId] = {
                    folderId: folder.folderId,
                    resourceId: folder.resourceId,
                };
            }
            else {
                delete newState[folder.resourceId];
            }
            getSubFolderRecursively(folder)
                .filter((subFolder) => subFolder.folderId !== folder.folderId)
                .forEach((folder) => delete newState[folder.resourceId]);
            handleFolderSelectedProp(Object.keys(newState).map(function (key) {
                return {
                    folderId: newState[key].folderId,
                    resourceId: newState[key].resourceId,
                };
            }));
            return newState;
        });
        if (selected) {
            setOpeningFolders((prevState) => {
                const newState = Object.assign({}, prevState);
                for (const affectedFolder of affectedFolders) {
                    newState[affectedFolder.resourceId] = {
                        folderId: affectedFolder.folderId,
                        resourceId: affectedFolder.resourceId,
                    };
                }
                return newState;
            });
        }
    }), [
        fetchSubFolderRecursively,
        handleFolderSelectedProp,
        getSubFolderRecursively,
    ]);
    const calculateMaximumShowingNames = (0, react_1.useCallback)((names, maximumNumShowingCharacters) => {
        var _a, _b;
        let sumUpNumberCharacter = (_b = (_a = names[0]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0;
        for (let i = 1; i < names.length; i++) {
            if (sumUpNumberCharacter + names[i].length >
                maximumNumShowingCharacters) {
                return i;
            }
            sumUpNumberCharacter += names[i].length;
        }
        return names.length;
    }, []);
    (0, react_1.useEffect)(function updateSelectedFoldersChip() {
        (() => tslib_1.__awaiter(this, void 0, void 0, function* () {
            const folderList = Object.values(selectedParentFolders);
            if (folderList.length === 0) {
                setSelectedChips({
                    showingNames: [],
                    hidingNames: [],
                });
            }
            else if (folderList.some((folder) => folder.folderId === allFolderId)) {
                setSelectedChips({
                    showingNames: [rootFolder.name],
                    hidingNames: [],
                });
            }
            else {
                const folders = yield Promise.all(folderList.map(({ folderId }) => serviceApi.fetchFolderById(Number(folderId))));
                const folderNames = folders.map((folder) => folder.data.name);
                const maximumShowingNames = calculateMaximumShowingNames(folderNames, maximumShowingCharacters);
                setSelectedChips({
                    showingNames: folderNames.slice(0, maximumShowingNames),
                    hidingNames: folderNames.slice(maximumShowingNames),
                });
            }
        }))();
    }, [
        serviceApi,
        selectedParentFolders,
        setSelectedChips,
        maximumShowingCharacters,
    ]);
    return {
        folders,
        handleFolderSelected,
        handleFolderOpening,
        selectedFolders,
        selectedParentFolders,
        openingFolders,
        selectedFolderChips,
    };
}
exports.useFolderRestrictions = useFolderRestrictions;
//# sourceMappingURL=useFolderRestrictions.js.map